//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import angular from 'angular';

import { addItemWithoutApproval } from '@fanreels/fanreelsApi';
import { ICuralateStore } from '@models/AppState';
import Cuid from '@models/Cuid';
import { getSelectedReelIds } from '@uia/approve-for-galleries/getSelectedReelIds';
import { UIA_RIGHTS_REQUEST } from '@uia/rights-request/rights-request-constants';
import { IRightsRequest } from '@uia/rights-request/rights-request-model';
import { selectedItemActions } from '@uia/selected-item/selected-item-actions';
import {
  requestMultiHandleRightsGraphApi,
  requestRights,
} from 'app/bulk-actions/universal-image-actions-api';
import { ReduxDispatch } from 'app/curalate.store';
import { getMediaItemMetadata } from '@apis/universal-image-actions/universalImageActionsApi';
import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';
import { getHasMultiHandleInstagramEnabled } from '@account-settings/redux/facebook-instagram/selectors/getMultiHandleFeature';
import { IMultiHandleRightsGraphApiRequest } from '@apis/rights-management/rightsManagementApiModels';

const setGraphRequestRights = (canRequest: boolean) => {
  return {
    type: UIA_RIGHTS_REQUEST.GRAPH_RIGHTS_CAPABILITY,
    payload: canRequest,
  };
};

const bypassRightsRequest = (skipRights: boolean) => {
  return {
    type: UIA_RIGHTS_REQUEST.BYPASS_RIGHTS_REQUEST,
    payload: skipRights,
  };
};

const setCustomRightsRequest = (newVal: IRightsRequest) => {
  return {
    type: UIA_RIGHTS_REQUEST.SET_CUSTOM_RIGHTS_REQUEST,
    payload: {
      ...newVal,
    },
  };
};

const graphRightsRequestStarted = () => {
  return {
    type: UIA_RIGHTS_REQUEST.GRAPH_RIGHTS_REQUEST_STARTED,
  };
};

const graphRightsRequestEnded = () => {
  return {
    type: UIA_RIGHTS_REQUEST.GRAPH_RIGHTS_REQUEST_ENDED,
  };
};

const rightsRequestStarted = () => {
  return {
    type: UIA_RIGHTS_REQUEST.RIGHTS_REQUEST_STARTED,
  };
};

const rightsRequestEnded = () => {
  return {
    type: UIA_RIGHTS_REQUEST.RIGHTS_REQUEST_ENDED,
  };
};

export const setRightsStatusChanged = (lastUpdatedStatus?: RIGHTS_STATUS) => ({
  type: UIA_RIGHTS_REQUEST.RIGHTS_STATUS_CHANGED,
  payload: lastUpdatedStatus,
});

const sendGraphRightsRequest =
  (cuid: Cuid, params: IRightsRequest) => async (dispatch: ReduxDispatch) => {
    dispatch(graphRightsRequestStarted());

    await requestMultiHandleRightsGraphApi({
      partialCuid: Cuid.toPartial(cuid),
      params,
    });

    dispatch(graphRightsRequestEnded());

    const selectedItemMetadata = await getMediaItemMetadata(cuid);
    dispatch(selectedItemActions.receiveItemMetadata(selectedItemMetadata));
  };

const sendRightsRequest =
  (cuid: Cuid, params: IRightsRequest) => async (dispatch: ReduxDispatch) => {
    dispatch(rightsRequestStarted());
    await requestRights(cuid, params);
    dispatch(rightsRequestEnded());

    const selectedItemMetadata = await getMediaItemMetadata(cuid);
    dispatch(selectedItemActions.receiveItemMetadata(selectedItemMetadata));
  };

const startSkipRightsRequest = () => {
  return {
    type: UIA_RIGHTS_REQUEST.SKIP_RIGHTS_REQUEST_STARTED,
  };
};

const finishSkipRightsRequest = () => {
  return {
    type: UIA_RIGHTS_REQUEST.SKIP_RIGHTS_REQUEST_ENDED,
  };
};

const skipRightsRequest =
  (cuid: Cuid) =>
  async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
    dispatch(startSkipRightsRequest());

    const addItemWithoutApprovalPromises = getSelectedReelIds(getState()).map(
      (reelId) => addItemWithoutApproval(cuid, reelId),
    );
    await Promise.all(addItemWithoutApprovalPromises);

    dispatch(finishSkipRightsRequest());
  };

export default {
  setGraphRequestRights,
  bypassRightsRequest,
  setCustomRightsRequest,
  sendGraphRightsRequest,
  sendRightsRequest,
  setRightsStatusChanged,
  skipRightsRequest,
};

angular.module('curalate').service('RightsRequestActions', [
  function () {
    this.setGraphRequestRights = setGraphRequestRights;
    this.bypassRightsRequest = bypassRightsRequest;
    this.setCustomRightsRequest = setCustomRightsRequest;
    this.sendGraphRightsRequest = sendGraphRightsRequest;
    this.sendRightsRequest = sendRightsRequest;
    this.setRightsStatusChanged = setRightsStatusChanged;
    this.skipRightsRequest = skipRightsRequest;
  },
]);
